import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Lock, Mail, CheckCircle2, AlertCircle } from 'lucide-react';

const errMsg = (e) => { const d = e?.response?.data?.detail; return typeof d === 'string' ? d : Array.isArray(d) ? d.map(x => x?.msg).join(' ') : 'Erreur, réessayez.'; };

function Banner({ ok, text }) {
    const cls = ok ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-700';
    const Icon = ok ? CheckCircle2 : AlertCircle;
    return <div className={`flex items-center gap-2 p-3 rounded-lg border text-sm ${cls}`} data-testid={ok ? 'reset-success' : 'reset-error'}><Icon className="w-4 h-4 shrink-0" /><span>{text}</span></div>;
}

function ForgotForm() {
    const [email, setEmail] = useState('');
    const [msg, setMsg] = useState(null);
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault(); setBusy(true); setMsg(null);
        try { const r = await axios.post(`${API_URL}/api/auth/forgot-password`, { email }); setMsg({ ok: true, text: r.data.message }); }
        catch (err) { setMsg({ ok: false, text: errMsg(err) }); }
        setBusy(false);
    };
    return (
        <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
            <p className="text-sm text-gray-600">Saisissez l'email de votre compte. Vous recevrez un lien valable 30 minutes, utilisable une seule fois.</p>
            {msg && <Banner {...msg} />}
            <div className="space-y-2">
                <Label className="text-gray-600 text-xs">Email</Label>
                <div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="pl-10" placeholder="vous@exemple.fr" data-testid="forgot-email-input" /></div>
            </div>
            <Button type="submit" disabled={busy} className="w-full bg-[#6366F1] hover:bg-[#4F46E5] text-white" data-testid="forgot-submit-btn">{busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Recevoir le lien</Button>
        </form>
    );
}

function ResetForm({ token }) {
    const [pwd, setPwd] = useState('');
    const [pwd2, setPwd2] = useState('');
    const [msg, setMsg] = useState(null);
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault(); setMsg(null);
        if (pwd.length < 8) return setMsg({ ok: false, text: 'Au moins 8 caractères.' });
        if (pwd !== pwd2) return setMsg({ ok: false, text: 'Les mots de passe ne correspondent pas.' });
        setBusy(true);
        try { const r = await axios.post(`${API_URL}/api/auth/reset-password`, { token, password: pwd }); setMsg({ ok: true, text: r.data.message }); setPwd(''); setPwd2(''); }
        catch (err) { setMsg({ ok: false, text: errMsg(err) }); }
        setBusy(false);
    };
    return (
        <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
            {msg && <Banner {...msg} />}
            {[['Nouveau mot de passe', pwd, setPwd, 'reset-password-input'], ['Confirmation', pwd2, setPwd2, 'reset-password-confirm-input']].map(([l, v, set, id]) => (
                <div key={id} className="space-y-2">
                    <Label className="text-gray-600 text-xs">{l}</Label>
                    <div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input type="password" required value={v} onChange={e => set(e.target.value)} className="pl-10" data-testid={id} /></div>
                </div>
            ))}
            <Button type="submit" disabled={busy} className="w-full bg-[#6366F1] hover:bg-[#4F46E5] text-white" data-testid="reset-submit-btn">{busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Enregistrer le mot de passe</Button>
        </form>
    );
}

export default function ResetPasswordPage() {
    const [params] = useSearchParams();
    const token = params.get('token');
    return (
        <div className="min-h-screen flex items-center justify-center bg-[#F5F7FA] p-6">
            <div className="w-full max-w-md space-y-6 bg-white rounded-2xl border border-gray-200 p-8">
                <h1 className="text-xl font-semibold text-gray-900">{token ? 'Choisir un nouveau mot de passe' : 'Mot de passe oublié'}</h1>
                {token ? <ResetForm token={token} /> : <ForgotForm />}
                <Link to="/auth" className="block text-center text-sm text-[#6366F1]" data-testid="reset-back-login">Retour à la connexion</Link>
            </div>
        </div>
    );
}
